/**
 * Vs-computer games, fully in the browser. Holds the authoritative state,
 * plays bot moves on a readable schedule, and saves after every change so a
 * refresh resumes exactly where you left off. The human is always seat 0.
 */
import { applyAction, chooseBotAction, legalActions, newGame, redactFor } from '../../engine';
import type { Action, BotLevel, GameState } from '../../engine';
import { BOT_NAMES } from '../../worker/protocol';
import { MAX_OPPONENTS } from '../brand';
import { KEYS, load, remove, save } from '../lib/storage';
import { botDelay } from './pacing';
import type { SeatInfo } from './types';

export const HUMAN = 0;

export interface SoloSave {
  v: 2;
  state: GameState;
  humanName: string;
}

export interface SoloSetup {
  /** How many computer opponents (1..5). */
  opponents: number;
  level: BotLevel;
  holes: number;
}

export const DEFAULT_SETUP: SoloSetup = { opponents: 2, level: 'medium', holes: 9 };

/** Read a stored setup, tolerating older shapes (the cast-id list). */
export function normalizeSetup(raw: unknown): SoloSetup {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const n = typeof o.opponents === 'number' ? o.opponents : Array.isArray(o.opponents) ? o.opponents.length : DEFAULT_SETUP.opponents;
  const level = o.level === 'easy' || o.level === 'medium' || o.level === 'hard' ? o.level : DEFAULT_SETUP.level;
  const holes = typeof o.holes === 'number' && o.holes >= 1 && o.holes <= 18 ? o.holes : DEFAULT_SETUP.holes;
  return { opponents: Math.max(1, Math.min(MAX_OPPONENTS, Math.round(n))), level, holes };
}

/** Bot names for a table, skipping the human's name. */
export function botNames(count: number, humanName: string): string[] {
  const taken = humanName.trim().toLowerCase();
  return BOT_NAMES.filter((n) => n.toLowerCase() !== taken).slice(0, count);
}

export interface Timers {
  set: (fn: () => void, ms: number) => unknown;
  clear: (h: unknown) => void;
}

const browserTimers: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export function randomSeed(): number {
  try {
    return crypto.getRandomValues(new Uint32Array(1))[0]!;
  } catch {
    return Math.floor(Math.random() * 2 ** 32);
  }
}

export function createSolo(setup: SoloSetup, humanName: string, seed = randomSeed(), levels?: BotLevel[]): SoloSave {
  const count = Math.max(0, Math.min(MAX_OPPONENTS, Math.floor(setup.opponents)));
  if (count === 0) throw new Error('At least one opponent');
  const names = botNames(count, humanName);
  const players = [
    { id: 'you', name: humanName || 'You', isBot: false },
    ...names.map((name, i) => ({ id: `bot-${i + 1}`, name, isBot: true, botLevel: levels?.[i] ?? setup.level })),
  ];
  const state = newGame({ seed, players, totalHoles: setup.holes });
  return { v: 2, state, humanName };
}

/** Load the saved game. Version 1 saves (the old cast) keep playing with the new bot names. */
export function loadSolo(): SoloSave | null {
  const s = load<(Omit<SoloSave, 'v'> & { v: number }) | null>(KEYS.solo, null);
  if (!s || !s.state || !Array.isArray(s.state.players)) return null;
  if (s.v === 2) return s as SoloSave;
  if (s.v !== 1) return null;
  const names = botNames(5, s.humanName ?? '');
  let k = 0;
  const players = s.state.players.map((p) => (p.isBot ? { ...p, name: names[k++] ?? p.name } : p));
  return { v: 2, state: { ...s.state, players }, humanName: s.humanName ?? 'You' };
}

export function clearSolo(): void {
  remove(KEYS.solo);
}

export function soloSeats(save: SoloSave): SeatInfo[] {
  return save.state.players.map((p) => {
    return {
      name: p.name,
      isBot: p.isBot,
      ...(p.botLevel ? { level: p.botLevel } : {}),
      connected: true,
      autopilot: false,
      ready: true,
    };
  });
}

export interface LocalGameOptions {
  speed?: number;
  timers?: Timers;
  rand?: () => number;
  persist?: boolean;
  onChange?: (view: GameState) => void;
}

export class LocalGame {
  private data: SoloSave;
  private readonly timers: Timers;
  private readonly speed: number;
  private readonly rand: () => number;
  private readonly persist: boolean;
  private handle: unknown = null;
  private lastWasHuman = false;
  private stopped = true;
  private paused = false;
  onChange: ((view: GameState) => void) | undefined;

  constructor(data: SoloSave, opts: LocalGameOptions = {}) {
    this.data = data;
    this.timers = opts.timers ?? browserTimers;
    this.speed = opts.speed ?? 1;
    this.rand = opts.rand ?? Math.random;
    this.persist = opts.persist ?? true;
    this.onChange = opts.onChange;
  }

  get save(): SoloSave {
    return this.data;
  }

  /** Full state (tests); the UI only ever sees `view`. */
  get state(): GameState {
    return this.data.state;
  }

  get view(): GameState {
    return redactFor(this.data.state, HUMAN);
  }

  start(): void {
    this.stopped = false;
    this.schedule();
  }

  stop(): void {
    this.stopped = true;
    if (this.handle !== null) this.timers.clear(this.handle);
    this.handle = null;
  }

  /** Hold bot moves (coach steps, travel scenes); resume picks up where it left off. */
  setPaused(p: boolean): void {
    if (this.paused === p) return;
    this.paused = p;
    this.schedule();
  }

  /** A human move. Returns false (and changes nothing) if it is not legal right now. */
  act(action: Action): boolean {
    const st = this.data.state;
    const legal = action.type === 'nextHole'
      ? st.phase === 'holeOver'
      : legalActions(st, HUMAN).some((a) => sameAction(a, action));
    if (!legal) return false;
    this.lastWasHuman = action.type !== 'nextHole';
    this.commit(applyAction(st, action));
    return true;
  }

  /** Use the player's current name (they may have changed it on Home since this game began). */
  rename(name: string): void {
    if (!name || name === this.data.humanName) return;
    const players = this.data.state.players.map((p, i) => (i === HUMAN ? { ...p, name } : p));
    this.data = { ...this.data, humanName: name, state: { ...this.data.state, players } };
    if (this.persist) save(KEYS.solo, this.data);
  }

  /** Start a rematch with the same seats. */
  rematch(seed = randomSeed()): void {
    const bots = this.data.state.players.filter((p) => p.isBot);
    const levels = bots.map((p) => p.botLevel ?? 'medium');
    this.data = createSolo({ opponents: bots.length, level: levels[0] ?? 'medium', holes: this.data.state.totalHoles }, this.data.humanName, seed, levels);
    this.lastWasHuman = false;
    this.commit(this.data.state, true);
  }

  private commit(next: GameState, replaced = false): void {
    if (!replaced) this.data = { ...this.data, state: next };
    if (this.persist) save(KEYS.solo, this.data);
    this.onChange?.(this.view);
    this.schedule();
  }

  /** Who the computer should move next, if anyone. */
  nextBot(): { seat: number; action: Action } | null {
    const st = this.data.state;
    if (st.phase === 'holeOver' || st.phase === 'gameOver') return null;
    const bots = st.players.map((p, i) => ({ p, i })).filter(({ p, i }) => p.isBot && legalActions(st, i).length > 0);
    if (!bots.length) return null;
    // Tee off is simultaneous: pick a random bot that still has flips.
    const pick = st.phase === 'initialFlip' ? bots[Math.floor(this.rand() * bots.length)]! : bots[0]!;
    return { seat: pick.i, action: chooseBotAction(st, pick.i) };
  }

  private schedule(): void {
    if (this.handle !== null) this.timers.clear(this.handle);
    this.handle = null;
    if (this.stopped || this.paused) return;
    const next = this.nextBot();
    if (!next) return;
    const delay = botDelay({
      action: next.action,
      state: this.data.state,
      afterHuman: this.lastWasHuman,
      speed: this.speed,
      rand: this.rand,
    });
    this.handle = this.timers.set(() => {
      this.handle = null;
      if (this.stopped || this.paused) return;
      const now = this.nextBot();
      if (!now) return;
      this.lastWasHuman = false;
      this.commit(applyAction(this.data.state, now.action));
    }, delay);
  }
}

export function sameAction(a: Action, b: Action): boolean {
  if (a.type !== b.type) return false;
  if ('player' in a && 'player' in b && a.player !== b.player) return false;
  if ('slot' in a && 'slot' in b && a.slot !== b.slot) return false;
  return true;
}
